/**
 * BulkFilesPage — Shows results for bulk file name search.
 * Reads file names from query param `?names=a.xml,b.xml,...` (deduped),
 * fetches via POST /files/fetch, and displays one row per matching
 * (file name, execution) pair. File names link to the File Detail page.
 * Pagination is server-side: page changes re-dispatch the fetch and
 * pagesCount comes from the API's totalPages (never derived from item count).
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import BreadcrumbGroup from '@cloudscape-design/components/breadcrumb-group';
import Button from '@cloudscape-design/components/button';
import Header from '@cloudscape-design/components/header';
import Pagination from '@cloudscape-design/components/pagination';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import Table from '@cloudscape-design/components/table';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { fetchFiles, clearFile } from 'src/store/slices/file/fileSlice';
import { dedupeNames, buildFileExecutionRows } from './filesHelpers';
import { DEFAULT_PAGE_SIZE, FILE_ROW_COLUMN_DEFINITIONS } from './bulkFilesColumns';

const MAX_FILE_NAMES = 100;

export const BulkFilesPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const fileNames = useMemo(() => {
    const raw = searchParams.get('names') || '';
    // searchParams.get already URL-decodes; decoding again would double-decode
    const parsed = raw
      .split(',')
      .map((n) => n.trim())
      .filter(Boolean);
    return dedupeNames(parsed).slice(0, MAX_FILE_NAMES);
  }, [searchParams]);

  const { data, loading, error } = useAppSelector((state) => state.file);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(DEFAULT_PAGE_SIZE);

  // Server-side pagination: re-dispatch on page change
  useEffect(() => {
    if (fileNames.length === 0) return;
    dispatch(fetchFiles({ fileNames, pageNumber: currentPage, pageSize }));
  }, [dispatch, fileNames, currentPage, pageSize]);

  // Snap back to page 1 if a refresh shrinks the result set below the current page
  useEffect(() => {
    const totalPagesFromApi = data?.totalPages ?? 0;
    if (totalPagesFromApi > 0 && currentPage > totalPagesFromApi) {
      setCurrentPage(1);
    }
  }, [data, currentPage]);

  useEffect(() => () => { dispatch(clearFile()); }, [dispatch]);

  const refresh = () => {
    if (fileNames.length === 0) return;
    dispatch(fetchFiles({ fileNames, pageNumber: currentPage, pageSize }));
  };

  const executions = data?.executions ?? [];
  const totalPages = data?.totalPages ?? 0;
  const notFound = data?.notFound ?? [];

  const rows = useMemo(
    () => buildFileExecutionRows(fileNames, executions),
    [fileNames, executions],
  );

  return (
    <Box padding="l">
      <SpaceBetween size="l">
        <BreadcrumbGroup
          items={[
            { text: 'Beacon', href: '/' },
            { text: 'File search results', href: '/files' },
          ]}
          onFollow={(e) => {
            e.preventDefault();
            navigate(e.detail.href);
          }}
        />

        {fileNames.length === 0 && (
          <Alert type="warning" header="No file names provided">
            Go back to Search and enter up to {MAX_FILE_NAMES} file names.
          </Alert>
        )}

        {fileNames.length > 0 && (
          <Header
            variant="h1"
            counter={data ? `(${executions.length})` : undefined}
            actions={
              <Button iconName="refresh" onClick={refresh} disabled={loading}>
                Refresh
              </Button>
            }
          >
            File search results
          </Header>
        )}

        {error && !loading && (
          <Alert
            type="error"
            header="Failed to fetch file search results"
            action={<Button onClick={refresh}>Retry</Button>}
          >
            {error}
          </Alert>
        )}

        {notFound.length > 0 && (
          <Alert type="info" header={`No matches for ${notFound.length} file name(s)`}>
            {notFound.join(', ')}
          </Alert>
        )}

        {loading && (
          <Box textAlign="center" padding={{ vertical: 'xl' }}>
            <SpaceBetween size="s" alignItems="center">
              <Spinner size="large" />
              <Box color="text-body-secondary">Fetching executions…</Box>
            </SpaceBetween>
          </Box>
        )}

        {!loading && !error && data && executions.length === 0 && (
          <Box textAlign="center" padding={{ vertical: 'l' }} color="text-body-secondary">
            No results
          </Box>
        )}

        {!loading && executions.length > 0 && (
          <Table
            variant="container"
            resizableColumns
            sortingDisabled
            items={rows}
            trackBy="rowKey"
            columnDefinitions={FILE_ROW_COLUMN_DEFINITIONS}
            header={
              <Header variant="h2" counter={`(${rows.length})`}>
                Matched files
              </Header>
            }
            empty={
              <Box textAlign="center" color="text-body-secondary">
                No results
              </Box>
            }
          />
        )}

        {totalPages > 1 && (
          <Box textAlign="center">
            <Pagination
              currentPageIndex={currentPage}
              pagesCount={totalPages}
              disabled={loading}
              onChange={({ detail }) => setCurrentPage(detail.currentPageIndex)}
            />
          </Box>
        )}
      </SpaceBetween>
    </Box>
  );
};
